import {Component, OnInit, ViewChild} from '@angular/core';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {SensorsRoutesEnum} from '../services/navigation/sensors-routes.enum';
import {ApiSensorResponse} from '../services/http/api-sensor-response';
import {MatSort, SortDirection} from '@angular/material/sort';
import {catchError, debounce, filter, Observable, of, Subject, switchMap, takeUntil, timer} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiSensorsListResponse} from '../services/http/api-sensors-list-response';
import {ApiSensorsListPaginatedQueryParams} from '../services/http/api-sensors-list-paginated-query-params';
import {SensorRestApiService} from '../services/http/sensor-rest-api.service';
import {PageEvent} from '@angular/material/paginator';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {UserRolesEnum} from '../../../core/services/user/user-roles.enum';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslocoService} from '@jsverse/transloco';
import {ApiEditSensorKnownErrorsEnum} from '../services/http/api-edit-sensor-known-errors.enum';
import {FormBuilder, FormControl} from '@angular/forms';
import {manufactureDateRangeValidator} from '../services/validator/manufacture-date-range.validator';
import {HttpUtils, Utils} from '@astek/utils';
import {ApiRefreshTimerEnum} from '../../../core/services/http/api-refresh-timer.enum';
import {SensorStatusEnum} from '../enum/sensor-status.enum';
import {expirationDateRangeValidator} from '../services/validator/expiration-date-range.validator';
import {usedDateRangeValidator} from '../services/validator/used-date-range.validator';
import {createDateRangeValidator} from '../services/validator/create-date-range.validator';
import {SelectionModel} from '@angular/cdk/collections';
import {ApiSendBundleRequest} from '../services/http/api-send-bundle-request';
import {ConfirmDialogComponent} from '../../../core/components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmDialogData} from '../../../core/components/confirm-dialog/confirm-dialog-data';
import {CreateOrderDialogComponent} from '../create-order-dialog/create-order-dialog.component';
import {CreateOrderDialogData} from '../create-order-dialog/create-order-dialog-data';
import {ApiCreateOrderRequest} from '../services/http/api-create-order-request';
import {ApiIndicateSterilizedRequest} from '../services/http/api-indicate-sterilized-request';

@Component({
  selector: 'xp-sensors-list',
  templateUrl: './sensors-list.component.html',
  styleUrls: ['../../crud.common.scss', './sensors-list.component.scss'],
})
export class SensorsListComponent implements OnInit {

  public isLoading: boolean = true;

  public sensors: Array<ApiSensorResponse> | null = null;
  public selection: SelectionModel<ApiSensorResponse> = new SelectionModel<ApiSensorResponse>(true, []);

  public itemsCount: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 10;

  public displayedColumns: Array<string> = [
    'selection',
    'uniqueProductKey',
    'cardboardNumber',
    'lotNumber',
    'faulty',
    'lost',
    'state',
    'status',
    'createdAt',
    'manufacturedAt',
    'expireAt',
  ];

  public readonly formGroup = this._formBuilder.group({
    search: new FormControl<string>(''),
    faulty: new FormControl<boolean | null>(null),
    lost: new FormControl<boolean | null>(null),
    state: new FormControl<boolean | null>(null),
    status: new FormControl<string | null>(null),
    manufacturedAtFrom: new FormControl<Date | null>(null),
    manufacturedAtTo: new FormControl<Date | null>(null),
    expireAtFrom: new FormControl<Date | null>(null),
    expireAtTo: new FormControl<Date | null>(null),
    usedAtFrom: new FormControl<Date | null>(null),
    usedAtTo: new FormControl<Date | null>(null),
    createdAtFrom: new FormControl<Date | null>(null),
    createdAtTo: new FormControl<Date | null>(null),
  }, {
    validators: [
      manufactureDateRangeValidator,
      expirationDateRangeValidator,
      usedDateRangeValidator,
      createDateRangeValidator,
    ],
  });

  public defaultSortOn: string = 'createdAt';
  public defaultSortOrder: SortDirection = 'asc';
  @ViewChild(MatSort)
  public sort: MatSort = new MatSort();

  private _requestApiOnChangeSubject: Subject<number> = new Subject<number>();

  constructor(
    private readonly _sensorRestApiService: SensorRestApiService,
    private readonly _authenticationService: AuthenticationService,
    private readonly _snackBar: MatSnackBar,
    private readonly _translocoService: TranslocoService,
    private readonly _formBuilder: FormBuilder,
    private readonly _dialog: MatDialog,
  ) {
    this._requestApiOnChangeSubject.pipe(
      takeUntilDestroyed(),
      debounce((dueTime: number) => timer(dueTime)),
      switchMap(() => this.requestSensors().pipe(
        takeUntil(this._requestApiOnChangeSubject),
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          return of(null);
        }),
      )),
    ).subscribe({
      next: (response: ApiSensorsListResponse | null) => {
        this.selection.clear();
        if (Utils.isNotNullOrUndefined(response)) {
          this.handleRequestSensorsResponse(response);
        }
      },
    });
    if (this.isAdmin) {
      this.displayedColumns.push('usedAt');
    }
  }

  public get isAdmin(): boolean {
    return this._authenticationService.user?.roles.includes(UserRolesEnum.ADMIN) ?? false;
  }

  public get batchCreateSensorRoute(): string {
    return '/' + RoutesEnum.SENSORS + '/' + SensorsRoutesEnum.BATCH_CREATE;
  }

  public get searchValue(): string {
    return this.formGroup.controls.search.value?.trim() ?? '';
  }

  public get statuses(): Array<{ key: string, value: SensorStatusEnum }> {
    return Object.entries(SensorStatusEnum).map(([key, value]) => {
      return {
        key: key,
        value: value,
      };
    });
  }

  public get selectedSensorsCreated(): Array<ApiSensorResponse> {
    return this.selection.selected.filter(
      (sensor: ApiSensorResponse) => sensor.status === SensorStatusEnum.CREATED as string,
    );
  }

  public get selectedSensorsManufactured(): Array<ApiSensorResponse> {
    return this.selection.selected.filter(
      (sensor: ApiSensorResponse) => sensor.status === SensorStatusEnum.MANUFACTURED as string,
    );
  }

  public get selectedSensorsSterilized(): Array<ApiSensorResponse> {
    return this.selection.selected.filter(
      (sensor: ApiSensorResponse) => sensor.status === SensorStatusEnum.STERILIZED as string,
    );
  }

  public get isSendBundleButtonDisabled(): boolean {
    if (Utils.isNullOrUndefined(this.selection)) {
      return true;
    }
    return this.selectedSensorsCreated.length === 0;
  }

  public get isIndicateSterilizedButtonDisabled(): boolean {
    if (Utils.isNullOrUndefined(this.selection)) {
      return true;
    }
    return this.selectedSensorsManufactured.length === 0;
  }

  public get isCreateOrderButtonDisabled(): boolean {
    if (Utils.isNullOrUndefined(this.selection)) {
      return true;
    }
    return this.selectedSensorsSterilized.length === 0;
  }

  public ngOnInit(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onSortChange(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onSelectionChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onPaginatorChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onSearchInput(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.KEYBOARD_INPUT);
  }

  public onDateChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public clearSearch(): void {
    this.pageIndex = 0;
    this.formGroup.controls.search.setValue('');
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.sensors ? this.sensors.length : 0;
    return numSelected === numRows;
  }

  public toggleAll(): void {
    if (Utils.isNull(this.sensors) || this.sensors.length < 1) {
      return;
    }

    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.sensors.forEach(row => this.selection.select(row));
  }

  public resetFilters(): void {
    this.formGroup.controls.search.setValue('');
    this.formGroup.controls.manufacturedAtFrom.setValue(null);
    this.formGroup.controls.manufacturedAtTo.setValue(null);
    this.formGroup.controls.expireAtFrom.setValue(null);
    this.formGroup.controls.expireAtTo.setValue(null);
    this.formGroup.controls.usedAtFrom.setValue(null);
    this.formGroup.controls.usedAtTo.setValue(null);
    this.formGroup.controls.faulty.setValue(null);
    this.formGroup.controls.lost.setValue(null);
    this.formGroup.controls.state.setValue(null);
    this.formGroup.controls.status.setValue(null);
    this.formGroup.controls.createdAtFrom.setValue(null);
    this.formGroup.controls.createdAtTo.setValue(null);
    this.clearSearch();
    this.formGroup.markAsPristine();
  }

  public confirmSendBundle(): void {
    const dialogData: ConfirmDialogData = {
      confirmTextKey: 'SENSORS.CONFIRM_DIALOG_SEND_BUNDLE.CONFIRM',
      cancelTextKey: 'SENSORS.CONFIRM_DIALOG_SEND_BUNDLE.CANCEL',
      message: 'SENSORS.CONFIRM_DIALOG_SEND_BUNDLE.MESSAGE',
      title: 'SENSORS.CONFIRM_DIALOG_SEND_BUNDLE.TITLE',
    };
    const dialogRef = this._dialog.open(
      ConfirmDialogComponent, {data: dialogData},
    );
    dialogRef.afterClosed().pipe(
      filter((confirmDelete: boolean) => confirmDelete),
      switchMap(() => this.requestSendBundle()),
    ).subscribe({
      next: () => {
        this._snackBar.open(
          this._translocoService.translate('SENSORS.CONFIRM_DIALOG_SEND_BUNDLE.SUCCESS'),
          '',
          {duration: 7000},
        );
        this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  public confirmIndicateSterilized(): void {
    const dialogData: ConfirmDialogData = {
      confirmTextKey: 'SENSORS.MARK_STERILIZED_CONFIRM_DIALOG.CONFIRM',
      cancelTextKey: 'SENSORS.MARK_STERILIZED_CONFIRM_DIALOG.CANCEL',
      message: 'SENSORS.MARK_STERILIZED_CONFIRM_DIALOG.MESSAGE',
      title: 'SENSORS.MARK_STERILIZED_CONFIRM_DIALOG.TITLE',
    };
    const dialogRef = this._dialog.open(
      ConfirmDialogComponent, {data: dialogData},
    );
    dialogRef.afterClosed().pipe(
      filter((confirmDelete: boolean) => confirmDelete),
      switchMap(() => this.requestIndicateSterilized()),
    ).subscribe({
      next: () => {
        this._snackBar.open(
          this._translocoService.translate('SENSORS.MARK_STERILIZED_CONFIRM_DIALOG.SUCCESS'),
          '',
          {duration: 7000},
        );
        this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  public confirmCreateOrder(): void {
    const dialogData: CreateOrderDialogData = {
      sensors: this.selectedSensorsSterilized,
    };
    const dialogRef = this._dialog.open(
      CreateOrderDialogComponent, {data: dialogData, panelClass: 'form-dialog'},
    );
    dialogRef.afterClosed().pipe(
      filter((data: any) => typeof data !== 'boolean'),
      switchMap((data: ApiCreateOrderRequest) => this.requestOrder(data)),
    ).subscribe({
      next: () => {
        this._snackBar.open(
          this._translocoService.translate('SENSORS.ORDER_DIALOG.SUCCESS'),
          '',
          {duration: 7000},
        );
        this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  private requestSensors(): Observable<ApiSensorsListResponse> {
    this.isLoading = true;
    const queryParams: ApiSensorsListPaginatedQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sort: this.sort?.active ?? this.defaultSortOn,
      order: this.sort?.direction ?? this.defaultSortOrder,
      search: this.searchValue,
      manufacturedAtFrom: this.formGroup.controls.manufacturedAtFrom.value,
      manufacturedAtTo: this.formGroup.controls.manufacturedAtTo.value,
      expireAtFrom: this.formGroup.controls.expireAtFrom.value,
      expireAtTo: this.formGroup.controls.expireAtTo.value,
      usedAtFrom: this.formGroup.controls.usedAtFrom.value,
      usedAtTo: this.formGroup.controls.usedAtTo.value,
      faulty: this.formGroup.controls.faulty.value,
      lost: this.formGroup.controls.lost.value,
      state: this.formGroup.controls.state.value,
      status: this.formGroup.controls.status.value,
      createdAtFrom: this.formGroup.controls.createdAtFrom.value,
      createdAtTo: this.formGroup.controls.createdAtTo.value,
    };
    return this._sensorRestApiService.requestSensorsListPaginated(queryParams);
  }

  private requestSendBundle(): Observable<void> {
    const data: ApiSendBundleRequest = {
      sensorIds: this.selectedSensorsCreated
        .map((sensor: ApiSensorResponse) => sensor.id),
    };

    return this._sensorRestApiService.requestSendBundle(data);
  }

  private requestIndicateSterilized(): Observable<void> {
    const data: ApiIndicateSterilizedRequest = {
      sensors: this.selectedSensorsManufactured
        .map((sensor: ApiSensorResponse) => sensor.id),
    };

    return this._sensorRestApiService.requestIndicateSterilized(data);
  }

  private requestOrder(data: ApiCreateOrderRequest): Observable<void> {
    return this._sensorRestApiService.requestCreateOrder(data);
  }

  private handleRequestSensorsResponse(response: ApiSensorsListResponse): void {
    this.isLoading = false;
    this.itemsCount = response.totalCount;
    this.sensors = response.sensors;
  }

  private handleError(error: HttpErrorResponse): void {
    this.isLoading = false;
    let messageKey: string = 'SHARED.ERRORS.UNKNOWN';
    if (HttpUtils.isSymfonyKnownError(
      error,
      ApiEditSensorKnownErrorsEnum,
      [HttpStatusCode.Forbidden],
    )) {
      messageKey = 'SENSORS.DELETE.' + HttpUtils.getSymfonyKnownErrorDetailMessage(error);
    }
    this._snackBar.open(
      this._translocoService.translate(messageKey),
      this._translocoService.translate('SHARED.CLOSE'),
    );
  }
}
